<script setup>
/** @typedef {import('../../domain/entities/ReconciliationTask.types').ReconciliationTask} ReconciliationTask */

import { i18n } from 'boot/i18n';
import UniversalTooltip from 'components/UI/UniversalTooltip';
import { Timestamp } from 'firebase/firestore';
import { useTime } from 'src/composables/time';
import { clients as useClientsStore } from 'src/store/clients';
import { computed } from 'vue';

import { TASK_STATUSES } from '../../domain/constants/reconciliation';

defineOptions({ name: 'ReconciliationTable' });

const props = defineProps({
  reconciliationTasks: {
    /** @type {import('vue').PropType<ReconciliationTask[]>} */
    type: Array,
    required: true,
  },
  isRunning: {
    type: Boolean,
    required: true,
  },
});

const { getLocaleTimeString, getLocaleString } = useTime();

function formatAppointmentLabel(appointment) {
  const startTime = (appointment.scheduledStartTimestamp instanceof Timestamp)
    ? appointment.scheduledStartTimestamp.toDate()
    : '';
  const endTimeMillis = (appointment.scheduledStartTimestamp instanceof Timestamp)
    ? appointment.scheduledStartTimestamp.toMillis()
      + (appointment.duration ?? 0) * 1000
    : '';
  const formattedStartTime = (startTime instanceof Date)
    ? getLocaleString(startTime, { options: { dateStyle: 'short', timeStyle: 'short' } })
    : '';
    // convert endTime from milliseconds to formatted string
  const formattedEndTime = (typeof endTimeMillis === 'number')
    ? getLocaleTimeString(new Date(endTimeMillis), { options: { hour: '2-digit', minute: '2-digit' } })
    : '';
  return `${formattedStartTime} - ${formattedEndTime}`;
} // formatAppointmentLabel()

const selectedAppointmentIds = computed(() => props.reconciliationTasks
  .filter((task) => task.selectedAppointmentId)
  .map((task) => task.selectedAppointmentId));

function getAppointmentOptions(availableAppointments, task) {
  if (!availableAppointments || availableAppointments.length === 0) {
    return [];
  }
  return availableAppointments
    .slice() // create a copy to avoid mutating original array
    .sort((a, b) => {
      const aStart = (a.scheduledStartTimestamp instanceof Timestamp)
        ? a.scheduledStartTimestamp.toMillis()
        : 0;
      const bStart = (b.scheduledStartTimestamp instanceof Timestamp)
        ? b.scheduledStartTimestamp.toMillis()
        : 0;
      return aStart - bStart;
    })
    .map((appt) => ({
      value: appt.id,
      label: formatAppointmentLabel(appt),
      caption: appt.sessionName,
      disable: (appt.id !== task.selectedAppointmentId && selectedAppointmentIds.value.includes(appt.id)),
    }));
} // getAppointmentOptions()

const clientsStore = useClientsStore();
const clientNamesMap = computed(() => clientsStore.clients.reduce((map, client) => {
  map[client.id] = client.clientName;
  return map;
}, {}));

const columns = [
  {
    name: 'taskStatus',
    label: '',
    field: 'taskStatus',
    align: 'center',
  },
  {
    name: 'userName',
    label: i18n.t('activityReconciliation.reconciliationTable.columns.userName'),
    field: (row) => row.sessionActivity.userName,
    align: 'left',
  },
  {
    name: 'startTimestamp',
    label: i18n.t('activityReconciliation.reconciliationTable.columns.startTimestamp'),
    align: 'left',
    field: (row) => row.sessionActivity.startTimestamp,
    format: (startTimestamp) => ((startTimestamp instanceof Timestamp)
      ? getLocaleString(startTimestamp, { options: { dateStyle: 'short', timeStyle: 'short' } })
      : ''),
  },
  {
    name: 'endTimestamp',
    label: i18n.t('activityReconciliation.reconciliationTable.columns.endTimestamp'),
    align: 'left',
    field: (row) => row.sessionActivity.endTimestamp,
    format: (endTimestamp) => ((endTimestamp instanceof Timestamp)
      ? getLocaleTimeString(endTimestamp, { options: { hour: '2-digit', minute: '2-digit' } })
      : ''),
  },
  {
    name: 'clientName',
    label: i18n.t('activityReconciliation.reconciliationTable.columns.client'),
    field: (row) => row.sessionActivity.client,
    format: (clientId) => (clientNamesMap.value[clientId] || ''),
    align: 'left',
  },
  {
    name: 'sessionName',
    label: i18n.t('activityReconciliation.reconciliationTable.columns.sessionName'),
    align: 'left',
    field: (row) => row.sessionActivity.sessionName,
  },
  {
    name: 'linkedAppointment',
    label: i18n.t('activityReconciliation.reconciliationTable.columns.linkedAppointment'),
    align: 'left',
    field: 'availableAppointments',
  },
];

</script>
<template>
  <q-table
    :rows="props.reconciliationTasks"
    :columns="columns"
    :pagination="{
      rowsPerPage: 50,
    }"
    :loading="props.isRunning"
    flat
    class="col-12"
  >
    <template #body-cell-linkedAppointment="cellProps">
      <q-td :props="cellProps">
        <div v-if="[TASK_STATUSES.PENDING, TASK_STATUSES.PROCESSING].includes(cellProps.row.taskStatus)" />
        <div
          v-else-if="cellProps.row.taskStatus === TASK_STATUSES.ALREADY_LINKED"
          class="text-grey-5"
        >
          {{ i18n.t('activityReconciliation.reconciliationTable.alreadyLinked') }}
        </div>
        <div
          v-else-if="cellProps.row.taskStatus === TASK_STATUSES.NO_MATCH"
          class="text-grey-5"
        >
          {{ i18n.t('activityReconciliation.reconciliationTable.noAvailableAppointments') }}
        </div>
        <q-select
          v-else
          :model-value="cellProps.row.selectedAppointmentId"
          :options="getAppointmentOptions(cellProps.value, cellProps.row)"
          :loading="isRunning"
          :disable="cellProps.row.taskStatus === TASK_STATUSES.LINKED"
          map-options
          emit-value
          clearable
          @update:model-value="(val) => cellProps.row.linkAppointment(val)"
          @clear="() => cellProps.row.unlinkAppointment()"
        >
          <template #option="scope">
            <q-item v-bind="scope.itemProps">
              <q-item-section>
                <q-item-label>
                  {{ scope.opt.label }}
                </q-item-label>
                <q-item-label caption>
                  {{ scope.opt.caption }}
                </q-item-label>
              </q-item-section>
            </q-item>
          </template>
        </q-select>
      </q-td>
    </template>
    <template #body-cell-taskStatus="cellProps">
      <q-td :props="cellProps">
        <q-icon
          v-if="cellProps.row.taskStatus === TASK_STATUSES.PENDING"
          name="pending"
          size="sm"
          color="grey-5"
        />
        <q-icon
          v-else-if="[TASK_STATUSES.NO_MATCH, TASK_STATUSES.ALREADY_LINKED].includes(cellProps.row.taskStatus)"
          name="block"
          size="sm"
          color="grey-5"
        />
        <q-icon
          v-else-if="cellProps.row.taskStatus === TASK_STATUSES.CONFIRMED"
          name="link"
          size="sm"
          color="primary"
        />
        <q-icon
          v-else-if="cellProps.row.taskStatus === TASK_STATUSES.LINKED"
          name="check"
          size="sm"
          color="positive"
        />
        <q-icon
          v-else-if="cellProps.row.taskStatus === TASK_STATUSES.CANCELLED"
          name="cancel"
          size="sm"
          color="negative"
        />
        <universal-tooltip
          v-else-if="cellProps.row.taskStatus === TASK_STATUSES.FAILED"
          show-tooltip
          :text="cellProps.row.errorMessage"
        >
          <q-icon
            name="running_with_errors"
            size="sm"
            color="negative"
          />
        </universal-tooltip>
        <q-spinner
          v-else-if="cellProps.row.taskStatus === TASK_STATUSES.PROCESSING"
          size="sm"
          color="primary"
        />
      </q-td>
    </template>
  </q-table>
</template>
